<template>
	<div>
		<content-loader :message="$t('loading')" v-if="client === null" />

		<page :title="client.name" icon="building" tabs :sidebar-collapsed="this.$store.getters['ui/appBarCollapsed']" v-else>
			<template v-slot:itemDetail v-if="canManageSettings">
				<div class="mr-auto">

				</div>
				<div>
					<b-dropdown class="text-white" variant="transparent" no-caret left>
						<template v-slot:button-content>
							<font-awesome-icon icon="ellipsis-v" class="timestamp text-white" />
						</template>
						<b-list-group flush>
							<b-dropdown-item-button button @click="openEdit" v-if="canManageSettings">Edit Client</b-dropdown-item-button>
							<b-dd-divider v-if="canManageGlobal" />
							<b-dropdown-item-button button @click="deleteClient" v-if="canManageGlobal">Delete Client</b-dropdown-item-button>
						</b-list-group>
					</b-dropdown>
				</div>
			</template>

			<add-client-modal id="edit-client" @submit="refresh" @hidden="closeEdit" :client="editClient" edit :can-manage-connections="canManageConnections" />

			<page-tabs slot="fullwidth" :sidebar-collapsed="this.$store.getters['ui/appBarCollapsed']">
				<b-row no-gutters>
					<b-col cols="11" xl="12">
						<page-tab :to="clientRoute('client')">{{ $t('client.dashboard') }}</page-tab>
						<page-tab :to="clientRoute('client-search')">Search</page-tab>
						<page-tab :to="clientRoute('client-notifications')">{{ $t('client.notifications') }}</page-tab>
						<page-tab :to="clientRoute('client-mag-templates')">MAG Templates</page-tab>
						<page-tab :to="clientRoute('client-monitors')" class="d-xl-inline-block d-none">{{ $t('client.monitors') }} ({{ monitorCount }})</page-tab>
						<page-tab :to="clientRoute('client-groups')" class="d-xl-inline-block d-none">{{ $t('client.groups') }}</page-tab>
						<page-tab :to="clientRoute('client-cw-settings')" v-if="cwEnabled" class="d-xl-inline-block d-none">ServiceNow Settings</page-tab>
					</b-col>
					<b-col cols="1" class="d-xl-none">
						<b-dd no-flip no-caret variant="transparent">
							<template v-slot:button-content>
								<font-awesome-icon icon="bars" />
							</template>
							<b-dd-item :active="isActive('client-monitors')" :to="clientRoute('client-monitors')">{{ $t('client.monitors') }} ({{ monitorCount }})</b-dd-item>
							<b-dd-item :active="isActive('client-groups')" :to="clientRoute('client-groups')">{{ $t('client.groups') }}</b-dd-item>
							<b-dd-item :active="isActive('client-cw-settings')" :to="clientRoute('client-cw-settings')" v-if="cwEnabled">ServiceNow Settings</b-dd-item>
						</b-dd>
					</b-col>
				</b-row>
			</page-tabs>

			<router-view />
		</page>
	</div>
</template>

<script>
import Page from '../../components/layout/Page';
import PageTabs from '../../components/PageTabs';
import PageTab from '../../components/PageTab';
import ContentLoader from '../../components/ContentLoader';
import ErrorModal from '../../mixins/ErrorModal';
import nprogress from 'nprogress';
import DeleteModal from '../../mixins/DeleteModal';
import AddClientModal from '../../components/clients/AddClientModal';

export default {
	components: {
		Page,
		PageTabs,
		PageTab,
		ContentLoader,
		AddClientModal
	},

	mixins: [ ErrorModal, DeleteModal ],
	mounted () {
		if (!this.canView) {
			this.$router.push('/');
		}
	},
	data () {
		return {
			editClient: null
		};
	},

	methods: {
		openEdit () {
			this.editClient = this.client;
			this.$bvModal.show('edit-client');
		},
		closeEdit () {
			this.editClient = null;
		},
		clientRoute (name) {
			return {
				name,
				params: {
					id: this.client.id
				}
			};
		},
		refresh () {
			this.$store.dispatch('sidebar/refresh', { client: this.client.id });
			this.$store.dispatch('clients/refresh')
				.then(data => {
					// not handling data locally
				})
				.catch(e => {
					this.showErrorModal(e, 'Error Refreshing Client Data');
				})
				.finally(() => {
					this.loading = false;
					nprogress.done();
				});
		},
		deleteClient () {
			const deleteClientMessage = 'Are you sure you want to delete this client? This will remove all associated data including devices, groups, monitors, notifications, client specific custom fields, permissions and logs. This action cannot be undone.';
			this.showDeleteModal('Delete Client', deleteClientMessage)
				.then(value => {
					if (value) {
						this.$store.dispatch('clients/deleteClient', this.client.id)
							.then(data => {
								this.$bvModal.msgBoxOk('Client was successfully deleted.', {
									headerBgVariant: 'success',
									headerTextVariant: 'white',
									okVariant: 'success',
									title: 'Success'
								});

								// side bar refresh on client delete, as there will be a lot of moving parts,
								// and the side bar may not have mounted yet, which would cause a data mismatch
								this.$store.dispatch('sidebar/refresh', {});
								// navigate back to the listing of clients
								this.$router.push('/clients');
							})
							.catch(e => {
								this.showErrorModal(e, 'Error Deleting Client');
							});
					}
				});
		},
		isActive (routeName) {
			return this.$route.name === routeName;
		},
		isManageSlug (slug) {
			return slug.includes('manage_client') ||
				slug === 'super_admin';
		}
	},

	computed: {
		client () {
			return this.$store.getters['clients/current'];
		},
		cwEnabled () {
			return this.$store.getters['settings/getSetting']('cw_plugin');
		},
		monitorCount () {
			return this.client.monitor_count || 0;
		},
		canView () {
			return this.$store.getters['auth/hasPermission']('view_client', true, this.client.id) ||
				this.$store.getters['auth/hasPermission']('manage_client*', true, this.client.id);
		},
		canManageSettings () {
			return this.$store.getters['auth/hasPermission']('manage_client_setting', true, this.client.id);
		},
		canManageGlobal () {
			return this.$store.getters['auth/hasPermission']('manage_client_setting');
		},
		canManageConnections () {
			return this.$store.getters['auth/hasPermission']('manage_connection');
		}
	},

	watch: {
		$route (to, from) {
			if (from.params.id !== to.params.id) {
				nprogress.done();

				if (!this.canView) {
					this.$router.push('/');
				}
			}
		}
	}
};
</script>
